import React from 'react';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { Well, Button, ButtonToolbar, Collapse, Label, Modal, MenuItem, SplitButton } from 'react-bootstrap';
import { Link } from 'react-router';
import urlMapping from '../app/URLMapping';
import AppPanel from '../fw/AppPanel.react';
import types from '../../common/model-api/types';
import DiffViewer from './DiffViewer';
import StepDiff, { stringifyDiffValue } from './StepDiff';
import {
  buildSnapshotValidationMarkdown,
  getDiffDisplayStatus,
  redactSnapshotExportValue,
} from './markdownExport';
import { parseSnapshotOutputPayload } from '../../common/snapshot-testing/computedDiffPayload';
import { getRunBaselineEligibility, RunBaselineEligibility } from '../../common/snapshot-testing/runBaselineEligibility';
import './FlowTestResultView.scss';
import CreateDerivedTestButton, { DeriveTestHandler } from './CreateDerivedTestButton';
import type { ReviewTestInvocation as TestInvocation, TestRunWire as TestRun } from './testcaseWireTypes';

// Testcase/run shapes come from the one browser model (QA-10 F10).

interface Group {
  stepId: string;
  stepName: any;
  stepType: string;
  writes: any[];
  hasMismatch: boolean;
}

interface FlowTestResultViewProps {
  intl: InjectedIntlProps['intl'];
  params: {
    organisationId: string;
    flowId: string;
  };
  testInvocation: TestInvocation;
  lastRun: TestRun | null;
  latestRunId?: string | null;
  groups: Group[];
  expectedSummary: any;
  actualSummary: any;
  hasSummaryMismatch: boolean;
  isDiffUnavailable?: boolean;
  mismatchCount: number;
  maxWritesLen: number;
  expandedGroupIndices: Set<number>;
  diffsCommand?: { action: string; id: number } | null;
  conflictError: string | null;
  onAccept: () => void;
  onManualUpdate: (expectedOutput: any, target?: EditorTarget) => Promise<EditorSaveOutcome> | void;
  onEditEnvironmentState: (environmentStateJson: string, target?: EditorTarget) => Promise<EditorSaveOutcome> | void;
  onDerive?: DeriveTestHandler;
  onDelete: () => void;
  onGroupToggle: (index: number) => void;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  onExpandAllDiffs: () => void;
  onCollapseAllDiffs: () => void;
  isHistoricalRun?: boolean;
  isRunUnavailable?: boolean;
}

/** The testcase identity and revision an editor session was opened against. */
export interface EditorTarget {
  id: string;
  realId?: string;
  version: number;
  testRunId?: string | null;
}

export interface EditorSaveOutcome {
  ok: boolean;
  error?: string | null;
}

interface EditorSession {
  token: number;
  target: EditorTarget;
  selectedRunId: string | null;
  saving: boolean;
  saveError: string | null;
}

type EditorSessionKey = 'manualUpdateSession' | 'editSnapshotSession';

function getEditorTargetKey(entity?: { id?: string; realId?: string } | null): string | null {
  return entity ? (entity.realId || entity.id || null) : null;
}

let nextEditorSessionToken = 1;

function createEditorSession(testInvocation: TestInvocation, lastRun?: { id?: string; realId?: string } | null): EditorSession {
  const selectedRunId = getEditorTargetKey(lastRun);
  return {
    token: nextEditorSessionToken++,
    target: { id: testInvocation.id, realId: testInvocation.realId, version: testInvocation.version as number, testRunId: selectedRunId },
    selectedRunId,
    saving: false,
    saveError: null,
  };
}

interface FlowTestResultViewState {
  manualUpdateSession: EditorSession | null;
  editSnapshotSession: EditorSession | null;
  showManualUpdateModal: boolean;
  manualUpdateJson: string;
  jsonError: string | null;
  showEditSnapshotModal: boolean;
  editSnapshotJson: string;
  editSnapshotJsonError: string | null;
}

/**
 * Formats a snapshot output for the JSON editor. GraphQL delivers outputs as JSON text, so decode
 * that wire string once before pretty-printing; text that is not JSON is shown unchanged.
 */
export function formatEditorJson(value: any): string {
  if (typeof value === 'string') {
    const decoded = parseSnapshotOutputPayload(value);
    return decoded === value ? value : JSON.stringify(decoded, null, 2);
  }
  return JSON.stringify(value, null, 2);
}

/** Whether the baseline service would accept this run, using the service's own rules. */
export function getRunAcceptEligibility(run: TestRun | null): RunBaselineEligibility {
  if (!run) {
    return getRunBaselineEligibility(null);
  }
  return getRunBaselineEligibility({
    status: run.status,
    error: run.error,
    actualOutput: parseSnapshotOutputPayload(run.actualOutput),
  });
}

const reviewMessages = defineMessages({
  acceptMissingOutput: { id: 'snapshotTesting.acceptIneligible.missingOutput', defaultMessage: 'Accept is unavailable: this run has no complete output to accept.' },
  acceptUnsafeStatus: { id: 'snapshotTesting.acceptIneligible.unsafeStatus', defaultMessage: 'Accept is unavailable for runs with status {status}.' },
  acceptRecaptureRequired: { id: 'snapshotTesting.acceptIneligible.recaptureRequired', defaultMessage: 'Accept is unavailable: this run requires source, infrastructure or snapshot recapture.' },
  acceptErrorBearing: { id: 'snapshotTesting.acceptIneligible.errorBearing', defaultMessage: 'Accept is unavailable: this run failed or reported an error.' },
  acceptIncomplete: { id: 'snapshotTesting.acceptIneligible.incomplete', defaultMessage: 'Accept is unavailable: this run output is incomplete.' },
  runUnavailableDisabled: { id: 'snapshotTesting.runUnavailableDisabled', defaultMessage: 'Actions are disabled because the requested run was not found.' },
  runUnavailable: { id: 'snapshotTesting.runUnavailable', defaultMessage: 'The requested run was not found. It may have expired or been deleted.' },
  runNotFound: { id: 'snapshotTesting.runNotFound', defaultMessage: 'Run not found' },
  saveNotConfirmed: { id: 'snapshotTesting.saveNotConfirmed', defaultMessage: 'The save was not confirmed. Your draft has been kept.' },
  editorVersionChanged: { id: 'snapshotTesting.editorVersionChanged', defaultMessage: 'This test or its selected run changed since you opened the editor (draft version {draftVersion}, current version {currentVersion}). Saving is blocked until you choose how to continue.' },
  editorRebaseDraft: { id: 'snapshotTesting.editorRebaseDraft', defaultMessage: 'Keep my draft on the current version' },
  editorReloadDraft: { id: 'snapshotTesting.editorReloadDraft', defaultMessage: 'Discard my draft and reload' },
  allowAdditionalChannelWrites: { id: 'snapshotTesting.allowAdditionalChannelWrites', defaultMessage: 'Allow additional replay channel writes' },
  allowAdditionalChannelWritesHelp: { id: 'snapshotTesting.allowAdditionalChannelWritesHelp', defaultMessage: 'When enabled, extra replay writes may be accepted automatically. This does not permit live writes. Save Snapshot to apply this setting.' },
});

const acceptIneligibleMessages: Record<string, { id: string; defaultMessage: string }> = {
  missing_output: reviewMessages.acceptMissingOutput,
  unsafe_status: reviewMessages.acceptUnsafeStatus,
  recapture_required: reviewMessages.acceptRecaptureRequired,
  error_bearing: reviewMessages.acceptErrorBearing,
  incomplete: reviewMessages.acceptIncomplete,
};

export function buildBrowserDebugExportPayload(props: FlowTestResultViewProps) {
  const { testInvocation, lastRun, groups, expectedSummary, actualSummary, hasSummaryMismatch, mismatchCount, maxWritesLen } = props;
  const markdownDiff = buildSnapshotValidationMarkdown(props, { includeOnlyDiffs: true });
  const redactedTestInvocation = redactSnapshotExportValue(testInvocation);
  const redactedLastRun = lastRun ? redactSnapshotExportValue(lastRun) : null;

  return {
    exportedAt: new Date().toISOString(),
    markdownDiff,
    testInvocation: {
      id: redactedTestInvocation.id,
      realId: redactedTestInvocation.realId,
      name: redactedTestInvocation.name,
      organisation_id: redactedTestInvocation.organisation_id,
      flow_id: redactedTestInvocation.flow_id,
      flowDef_id: redactedTestInvocation.flowDef_id,
      flowDefVersion_id: redactedTestInvocation.flowDefVersion_id,
      sourceFlowInvocation_id: redactedTestInvocation.sourceFlowInvocation_id,
      version: redactedTestInvocation.version,
      createdAt: redactedTestInvocation.createdAt,
      updatedAt: redactedTestInvocation.updatedAt,
      lastStatus: redactedTestInvocation.lastStatus,
      lastRunAt: redactedTestInvocation.lastRunAt,
      lastDuration: redactedTestInvocation.lastDuration,
      lastError: redactedTestInvocation.lastError,
      startType: redactedTestInvocation.startType,
    },
    testRun: redactedLastRun ? {
      id: redactedLastRun.id,
      realId: redactedLastRun.realId,
      status: redactedLastRun.status,
      createdAt: redactedLastRun.createdAt,
      error: redactedLastRun.error,
      duration: redactedLastRun.duration,
      expiresIn: redactedLastRun.expiresIn,
    } : null,
    runStatus: redactedLastRun?.status ?? null,
    expectedSummary: redactSnapshotExportValue(expectedSummary),
    actualSummary: redactSnapshotExportValue(actualSummary),
    hasSummaryMismatch,
    mismatchCount,
    maxWritesLen,
    groups: redactSnapshotExportValue(groups),
  };
}

// Stable DOM ids that link each JSON editor dialog, field, help text and error message (QA-10 F53/F54).
// Only one validation view is mounted per page, so these are unique within the page.
export const MANUAL_UPDATE_IDS = {
  title: 'snapshot-testing-manual-update-title',
  help: 'snapshot-testing-manual-update-help',
  field: 'snapshot-testing-manual-update-json',
  error: 'snapshot-testing-manual-update-error',
};
export const EDIT_SNAPSHOT_IDS = {
  title: 'snapshot-testing-edit-snapshot-title',
  help: 'snapshot-testing-edit-snapshot-help',
  field: 'snapshot-testing-edit-snapshot-json',
  error: 'snapshot-testing-edit-snapshot-error',
};

function focusField(field: HTMLTextAreaElement | null) {
  if (field && typeof field.focus === 'function') {
    field.focus();
  }
}

export class FlowTestResultView extends React.Component<FlowTestResultViewProps, FlowTestResultViewState> {
  manualUpdateField: HTMLTextAreaElement | null = null;
  editSnapshotField: HTMLTextAreaElement | null = null;
  private additionalWritesDraftCache: { text: string; enabled: boolean | null } | null = null;

  constructor(props: FlowTestResultViewProps) {
    super(props);
    this.state = {
      manualUpdateSession: null,
      editSnapshotSession: null,
      showManualUpdateModal: false,
      manualUpdateJson: '',
      jsonError: null,
      showEditSnapshotModal: false,
      editSnapshotJson: '',
      editSnapshotJsonError: null,
    };
  }

  private unmounted = false;

  componentWillReceiveProps(nextProps: FlowTestResultViewProps) {
    // A draft belongs to the testcase it was opened for. If the page now shows a different
    // testcase, invalidate open editors instead of retargeting the draft.
    const nextKey = getEditorTargetKey(nextProps.testInvocation);
    const { manualUpdateSession, editSnapshotSession } = this.state;
    if (manualUpdateSession && getEditorTargetKey(manualUpdateSession.target) !== nextKey) {
      this.handleCloseManualUpdate();
    }
    if (editSnapshotSession && getEditorTargetKey(editSnapshotSession.target) !== nextKey) {
      this.handleCloseEditSnapshot();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  isSessionStale(session: EditorSession | null): boolean {
    if (!session) return false;
    const selectedRunId = getEditorTargetKey(this.props.lastRun);
    const latestRunId = this.props.latestRunId !== undefined ? this.props.latestRunId : selectedRunId;
    return session.target.version !== this.props.testInvocation.version
      || session.selectedRunId !== selectedRunId
      || (session.target.testRunId || null) !== (latestRunId || null);
  }

  /**
   * Saves an editor draft against its pinned target and keeps the editor open until the save is
   * acknowledged. Failures keep the draft; responses for a closed or replaced session are ignored.
   */
  saveEditorSession(
    sessionKey: EditorSessionKey,
    submit: (target: EditorTarget) => Promise<EditorSaveOutcome> | void,
    close: () => void
  ) {
    const session = this.state[sessionKey];
    if (!session || session.saving || this.isSessionStale(session)) {
      return;
    }
    const { token, target } = session;
    this.setState({ [sessionKey]: Object.assign({}, session, { saving: true, saveError: null }) } as any);

    const isCurrent = () => {
      const current = this.state[sessionKey];
      return !this.unmounted && !!current && current.token === token;
    };
    const fail = (error?: string | null) => {
      if (!isCurrent()) return;
      const fallback = this.props.intl.formatMessage(reviewMessages.saveNotConfirmed);
      this.setState({ [sessionKey]: Object.assign({}, this.state[sessionKey], { saving: false, saveError: error || fallback }) } as any);
    };

    let submitted: Promise<EditorSaveOutcome> | void;
    try {
      submitted = submit(target);
    } catch (error) {
      fail(error && error.message);
      return Promise.resolve();
    }
    return Promise.resolve(submitted).then((outcome) => {
      if (!isCurrent()) return;
      if (outcome && outcome.ok) {
        close();
      } else {
        fail(outcome && outcome.error);
      }
    }, (error: any) => fail(error && error.message));
  }

  handleRebaseSession = (sessionKey: EditorSessionKey) => {
    const session = this.state[sessionKey];
    if (!session) return;
    this.setState({
      [sessionKey]: Object.assign({}, session, {
        target: Object.assign({}, session.target, { version: this.props.testInvocation.version, testRunId: this.props.latestRunId !== undefined ? this.props.latestRunId : getEditorTargetKey(this.props.lastRun) }),
        selectedRunId: getEditorTargetKey(this.props.lastRun),
        saveError: null,
      }),
    } as any);
  };

  handleOpenManualUpdate = () => {
    const { testInvocation, lastRun } = this.props;
    const currentOutput = lastRun?.actualOutput || testInvocation.expectedOutput || {};
    this.setState({
      manualUpdateSession: createEditorSession(testInvocation, lastRun),
      showManualUpdateModal: true,
      manualUpdateJson: formatEditorJson(currentOutput),
      jsonError: null,
    });
  };

  handleCloseManualUpdate = () => {
    this.setState({ showManualUpdateModal: false, jsonError: null, manualUpdateSession: null });
  };

  handleManualUpdateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (this.state.manualUpdateSession && this.state.manualUpdateSession.saving) return;
    this.setState({ manualUpdateJson: e.target.value, jsonError: null });
  };

  handleSaveManualUpdate = () => {
    const { manualUpdateJson } = this.state;
    let parsed: any;
    try {
      parsed = JSON.parse(manualUpdateJson);
    } catch (e: any) {
      this.setState({ jsonError: e.message }, () => focusField(this.manualUpdateField));
      return;
    }
    return this.saveEditorSession('manualUpdateSession', (target) => this.props.onManualUpdate(parsed, target), this.handleCloseManualUpdate);
  };

  handleExportDebuggingJson = () => {
    const { testInvocation } = this.props;
    const payload = buildBrowserDebugExportPayload(this.props);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `debugging-export-${testInvocation.name || 'report'}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleOpenEditSnapshot = () => {
    const { testInvocation } = this.props;
    const es = testInvocation.environmentState;
    const json = es ? JSON.stringify(typeof es === 'string' ? JSON.parse(es) : es, null, 2) : '{}';
    this.setState({ editSnapshotSession: createEditorSession(testInvocation, this.props.lastRun), showEditSnapshotModal: true, editSnapshotJson: json, editSnapshotJsonError: null });
  };

  handleCloseEditSnapshot = () => {
    this.setState({ showEditSnapshotModal: false, editSnapshotJsonError: null, editSnapshotSession: null });
  };

  getAdditionalChannelWritesDraft = (): boolean | null => {
    const { editSnapshotJson } = this.state;
    if (this.additionalWritesDraftCache && this.additionalWritesDraftCache.text === editSnapshotJson) {
      return this.additionalWritesDraftCache.enabled;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(editSnapshotJson);
    } catch (_error) {
      this.additionalWritesDraftCache = { text: editSnapshotJson, enabled: null };
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      this.additionalWritesDraftCache = { text: editSnapshotJson, enabled: null };
      return null;
    }

    const policy = parsed.snapshotPolicy;
    if (policy !== undefined && (!policy || typeof policy !== 'object' || Array.isArray(policy))) {
      this.additionalWritesDraftCache = { text: editSnapshotJson, enabled: null };
      return null;
    }
    const allowAdditionalChannelWrites = policy && policy.allowAdditionalChannelWrites;
    if (allowAdditionalChannelWrites !== undefined && typeof allowAdditionalChannelWrites !== 'boolean') {
      this.additionalWritesDraftCache = { text: editSnapshotJson, enabled: null };
      return null;
    }

    const enabled = allowAdditionalChannelWrites === true;
    this.additionalWritesDraftCache = { text: editSnapshotJson, enabled };
    return enabled;
  };

  handleAdditionalChannelWritesChange = (e: React.ChangeEvent<{ checked: boolean }>) => {
    const { editSnapshotJson, editSnapshotSession } = this.state;
    if (!editSnapshotSession || editSnapshotSession.saving || this.getAdditionalChannelWritesDraft() === null) return;
    const parsed = JSON.parse(editSnapshotJson);
    const policy = parsed.snapshotPolicy || {};
    const next = {
      ...parsed,
      snapshotPolicy: {
        ...policy,
        allowAdditionalChannelWrites: e.target.checked,
      },
    };
    const nextDraft = JSON.stringify(next, null, 2);
    this.additionalWritesDraftCache = null;
    this.setState({ editSnapshotJson: nextDraft, editSnapshotJsonError: null });
  };

  handleEditSnapshotChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (this.state.editSnapshotSession && this.state.editSnapshotSession.saving) return;
    this.setState({ editSnapshotJson: e.target.value, editSnapshotJsonError: null });
  };

  handleSaveEditSnapshot = () => {
    const { editSnapshotJson } = this.state;
    try {
      JSON.parse(editSnapshotJson); // validate
    } catch (e: any) {
      this.setState({ editSnapshotJsonError: e.message }, () => focusField(this.editSnapshotField));
      return;
    }
    return this.saveEditorSession('editSnapshotSession', (target) => this.props.onEditEnvironmentState(editSnapshotJson, target), this.handleCloseEditSnapshot);
  };

  renderEditorSessionStatus(sessionKey: EditorSessionKey, onReload: () => void) {
    const session = this.state[sessionKey];
    if (!session) return null;
    const isStale = !session.saving && this.isSessionStale(session);
    return (
      <div>
        {isStale && (
          <div className="alert alert-warning editor-session-stale" style={{ marginTop: '10px' }}>
            <FormattedMessage
              {...reviewMessages.editorVersionChanged}
              values={{ draftVersion: session.target.version, currentVersion: this.props.testInvocation.version }}
            />
            <div style={{ marginTop: '8px' }}>
              <Button bsSize="small" onClick={() => this.handleRebaseSession(sessionKey)}>
                <FormattedMessage {...reviewMessages.editorRebaseDraft} />
              </Button>{' '}
              <Button bsSize="small" onClick={onReload}>
                <FormattedMessage {...reviewMessages.editorReloadDraft} />
              </Button>
            </div>
          </div>
        )}
        {session.saveError && (
          <div className="alert alert-danger editor-save-error" style={{ marginTop: '10px' }}>
            {session.saveError}
          </div>
        )}
      </div>
    );
  }

  handleExportMarkdown = (includeOnlyDiffs: boolean = true) => {
    const { testInvocation } = this.props;
    const markdownContent = buildSnapshotValidationMarkdown(this.props, { includeOnlyDiffs });
    const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `snapshot-diff-${testInvocation.name || 'report'}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  render() {
    const {
      intl,
      params,
      testInvocation,
      lastRun,
      groups,
      expectedSummary,
      actualSummary,
      hasSummaryMismatch,
      isDiffUnavailable,
      mismatchCount,
      maxWritesLen,
      expandedGroupIndices,
      diffsCommand,
      onAccept,
      onDelete,
      onGroupToggle,
      onExpandAllGroups,
      onCollapseAllGroups,
      onExpandAllDiffs,
      onCollapseAllDiffs,
      isHistoricalRun,
      isRunUnavailable,
    } = this.props;
    const derivation = parseSnapshotOutputPayload(testInvocation.environmentState)?.derivation;
    const isBaselineEditDisabled = !!(isHistoricalRun || isRunUnavailable);
    const acceptEligibility = lastRun ? getRunAcceptEligibility(lastRun) : null;
    const acceptIneligibleTitle = acceptEligibility && !acceptEligibility.eligible
      ? intl.formatMessage(acceptIneligibleMessages[acceptEligibility.code], { status: lastRun ? lastRun.status : '' })
      : undefined;
    const baselineEditDisabledTitle = isRunUnavailable
      ? intl.formatMessage(reviewMessages.runUnavailableDisabled)
      : isHistoricalRun
        ? intl.formatMessage({ id: 'snapshotTesting.historicalRunDisabled', defaultMessage: 'Actions are disabled when viewing historical runs.' })
        : undefined;

    const { showManualUpdateModal, manualUpdateJson, jsonError, manualUpdateSession } = this.state;
    const { showEditSnapshotModal, editSnapshotJson, editSnapshotJsonError, editSnapshotSession } = this.state;

    const { flowId, organisationId } = params;

    const breadcrumbParams = [
      { type: types['organisations'], data: { realId: organisationId, id: organisationId, name: 'Organisation' } },
      { type: types['flows'], data: { realId: flowId, id: flowId, name: 'Flow' } },
      { type: { entity: () => 'snapshotTesting', getDefaultTitle: () => 'Validate' }, data: { name: 'Validate' } }
    ];

    return (
      <AppPanel
        breadcrumbParams={breadcrumbParams}
        title={intl.formatMessage({ id: 'snapshotTesting.validateTitle', defaultMessage: 'Validate Snapshot' })}
      >
        <div className="snapshot-validation-container">
          <div className="header-section">
            <div className="meta-info">
              <h3>
                <i className="fa fa-check-square-o" /> <FormattedMessage defaultMessage="Validate Snapshot" id="snapshotTesting.validateHeader" />
              </h3>
              <Well bsSize="small">
                <strong><FormattedMessage defaultMessage="Test:" id="snapshotTesting.metaTest" /></strong> {testInvocation.name}<br />
                <strong><FormattedMessage defaultMessage="Run At:" id="snapshotTesting.metaRunAt" /></strong> {lastRun ? lastRun.createdAt : <FormattedMessage defaultMessage="N/A" id="common.notAvailableShort" />}<br />
                <strong><FormattedMessage defaultMessage="Status:" id="snapshotTesting.metaStatus" /></strong> {lastRun ? lastRun.status : isRunUnavailable ? <FormattedMessage {...reviewMessages.runNotFound} /> : <FormattedMessage defaultMessage="Not run" id="snapshotTesting.notRun" />}<br />
                {testInvocation.sourceFlowInvocation_id && (
                  <span>
                    <strong><FormattedMessage defaultMessage="Original Invocation:" id="snapshotTesting.metaOriginalInvocation" /></strong>{' '}
                    <Link to={urlMapping.getUrl('flowInvocations', 'view', {
                      organisation_id: testInvocation.organisation_id,
                      flow_id: testInvocation.flow_id,
                      id: testInvocation.sourceFlowInvocation_id
                    })}>
                      <FormattedMessage defaultMessage="view invocation" id="snapshotTesting.viewInvocationLink" />
                    </Link>
                  </span>
                )}
                {derivation && derivation.sourceTestInvocationId && <div className="derived-test-lineage">
                  <strong><FormattedMessage id="snapshotTesting.derivedFrom" defaultMessage="Derived from:" /></strong>{' '}
                  <Link to={urlMapping.getUrl('snapshotTesting', 'validate', {
                    organisation_id: testInvocation.organisation_id,
                    flow_id: testInvocation.flow_id || params.flowId,
                    id: derivation.sourceTestInvocationId,
                  })}>{derivation.sourceName || derivation.sourceTestInvocationId}</Link>{' '}
                  <FormattedMessage id="snapshotTesting.derivedSourceVersion" defaultMessage="(version {version})" values={{ version: derivation.sourceVersion }} />
                  <p><FormattedMessage id="snapshotTesting.derivedIndependent" defaultMessage="This testcase is independent. Editing, accepting, running or deleting it does not change its source testcase." /></p>
                  {derivation.reason && <p>{derivation.reason}</p>}
                </div>}
              </Well>
            </div>
            <div className="actions">
              <ButtonToolbar>
                <Button
                  bsStyle="success"
                  disabled={!lastRun || isBaselineEditDisabled || !!acceptIneligibleTitle}
                  onClick={onAccept}
                  title={baselineEditDisabledTitle || acceptIneligibleTitle}
                >
                  <i className="fa fa-check" /> <FormattedMessage defaultMessage="Accept & Update" id="snapshotTesting.accept" />
                </Button>
                <Button
                  bsStyle="primary"
                  disabled={isBaselineEditDisabled}
                  onClick={this.handleOpenManualUpdate}
                  title={baselineEditDisabledTitle}
                >
                  <i className="fa fa-edit" /> <FormattedMessage defaultMessage="Manual Update" id="snapshotTesting.manualUpdate" />
                </Button>
                <Button
                  bsStyle="warning"
                  disabled={isBaselineEditDisabled || !testInvocation.environmentState}
                  onClick={this.handleOpenEditSnapshot}
                  title={baselineEditDisabledTitle}
                >
                  <i className="fa fa-sliders" /> <FormattedMessage defaultMessage="Edit Snapshot" id="snapshotTesting.editSnapshot" />
                </Button>
                {this.props.onDerive && <CreateDerivedTestButton
                  testInvocation={testInvocation}
                  onDerive={this.props.onDerive}
                  disabled={isBaselineEditDisabled || !testInvocation.environmentState}
                />}
                <SplitButton
                  bsStyle="default"
                  id="snapshotTesting-exportMarkdown"
                  title={<span><i className="fa fa-download" /> <FormattedMessage defaultMessage="Export Markdown" id="snapshotTesting.exportMarkdown" /></span>}
                  onClick={() => this.handleExportMarkdown()}
                >
                  <MenuItem onClick={() => this.handleExportMarkdown(true)}>
                    <FormattedMessage defaultMessage="Include only diffs" id="snapshotTesting.exportMarkdownDiffsOnly" />
                  </MenuItem>
                  <MenuItem onClick={this.handleExportDebuggingJson}>
                    <FormattedMessage defaultMessage="Debugging export" id="snapshotTesting.exportDebuggingJson" />
                  </MenuItem>
                </SplitButton>
                <Button bsStyle="danger" onClick={onDelete}>
                  <i className="fa fa-trash" /> <FormattedMessage defaultMessage="Delete" id="snapshotTesting.delete" />
                </Button>
              </ButtonToolbar>
              {this.props.conflictError && (
                <Well bsSize="small" className="conflict-error" role="alert" style={{ marginTop: '10px', backgroundColor: '#fcf8e3', borderColor: '#faebcc' }}>
                  <i className="fa fa-exclamation-triangle" style={{ color: '#8a6d3b', marginRight: '8px' }} />
                  <span style={{ color: '#8a6d3b' }}>{this.props.conflictError}</span>
                </Well>
              )}
            </div>
          </div>

          <div className="diff-section">
            <div className="section-header">
              <h4><FormattedMessage defaultMessage="Diff" id="snapshotTesting.diff" /></h4>
            </div>

            {hasSummaryMismatch && (
              <div className="summary-mismatch">
                <div className="summary-headers">
                  <h4><FormattedMessage defaultMessage="Expected Summary" id="snapshotTesting.expectedSummary" /></h4>
                  <h4><FormattedMessage defaultMessage="Actual Summary" id="snapshotTesting.actualSummary" /></h4>
                </div>
                <DiffViewer
                  actual={stringifyDiffValue(actualSummary)}
                  expected={stringifyDiffValue(expectedSummary)}
                  filename="summary.json"
                />
              </div>
            )}

            <div className="timeline-view">
              <div className="timeline-header">
                <h4>
                  <FormattedMessage defaultMessage="Step Timeline" id="snapshotTesting.timeline" />
                  {maxWritesLen > 0 && (
                    <small>
                      (
                      <FormattedMessage
                        defaultMessage="{count, plural, one {# write} other {# writes}}"
                        id="snapshotTesting.writeCount"
                        values={{ count: maxWritesLen }}
                      />
                      {mismatchCount > 0 && (
                        <span>
                          , <FormattedMessage
                            defaultMessage="{count} mismatched"
                            id="snapshotTesting.mismatchCount"
                            values={{ count: mismatchCount }}
                          />
                        </span>
                      )}
                      )
                    </small>
                  )}
                </h4>
                <div className="btn-group btn-group-xs">
                  <Button onClick={onExpandAllGroups}>
                    <FormattedMessage defaultMessage="Expand Steps" id="snapshotTesting.expandSteps" />
                  </Button>
                  <Button onClick={onCollapseAllGroups}>
                    <FormattedMessage defaultMessage="Collapse Steps" id="snapshotTesting.collapseSteps" />
                  </Button>
                  <Button onClick={onExpandAllDiffs}>
                    <FormattedMessage defaultMessage="Expand Diffs" id="snapshotTesting.expandDiffs" />
                  </Button>
                  <Button onClick={onCollapseAllDiffs}>
                    <FormattedMessage defaultMessage="Collapse Diffs" id="snapshotTesting.collapseDiffs" />
                  </Button>
                </div>
              </div>

              {isRunUnavailable ? (
                <div className="no-writes run-unavailable">
                  <FormattedMessage {...reviewMessages.runUnavailable} />
                </div>
              ) : isDiffUnavailable ? (
                <div className="no-writes">
                  <FormattedMessage defaultMessage="Diff unavailable for this run." id="snapshotTesting.diffUnavailable" />
                </div>
              ) : groups.length > 0 ? (
                <div className="panel-group" id="snapshot-accordion">
                  {groups.map((group, idx) => {
                    const isOpen = expandedGroupIndices.has(idx);
                    const groupStatus = getDiffDisplayStatus(group.hasMismatch, lastRun ? lastRun.status : undefined);
                    return (
                      <div className={`panel panel-${group.hasMismatch ? 'danger' : 'default'}`} key={idx}>
                        <button
                          aria-controls={`snapshot-testing-validation-group-${idx}`}
                          aria-expanded={isOpen}
                          className="panel-heading"
                          onClick={() => onGroupToggle(idx)}
                          type="button"
                        >
                          <span className="panel-title-content">
                            <span>
                              <strong>{group.stepName}</strong>
                              {group.stepType && <span className="text-muted"> ({group.stepType})</span>}
                            </span>
                            <Label bsStyle={groupStatus.bsStyle}>
                              {groupStatus.label}
                            </Label>
                          </span>
                        </button>
                        <Collapse in={isOpen}>
                          <div className="panel-body" id={`snapshot-testing-validation-group-${idx}`}>
				{group.writes.map((write, wIdx) => (
                  <StepDiff
                    actionName={write.actionName}
                    fetchName={write.fetchName}
                    expected={typeof write.expectedPreview !== 'undefined' ? write.expectedPreview : write.expected}
                    actual={typeof write.actualPreview !== 'undefined' ? write.actualPreview : write.actual}
                    index={write.index}
                    isMismatched={write.isMismatched}
                    status={getDiffDisplayStatus(write.isMismatched, lastRun ? lastRun.status : undefined)}
                    diffsCommand={diffsCommand || undefined}
                    key={wIdx}
                    stepId={write.stepId}
                    stepName={write.stepName}
                    stepType={write.stepType}
                  />
                ))}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-writes">
                  <FormattedMessage defaultMessage="No writes recorded." id="snapshotTesting.noWrites" />
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal show={showManualUpdateModal} onHide={this.handleCloseManualUpdate} aria-labelledby={MANUAL_UPDATE_IDS.title}>          <Modal.Header closeButton>
            <Modal.Title id={MANUAL_UPDATE_IDS.title}>
              <FormattedMessage defaultMessage="Edit Expected Output" id="snapshotTesting.editExpectedOutput" />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p id={MANUAL_UPDATE_IDS.help}>
              <FormattedMessage
                defaultMessage="Edit the JSON below to set the expected output. Changes will be normalized before saving."
                id="snapshotTesting.manualUpdateHelp"
              />
            </p>
            <textarea
              aria-describedby={jsonError ? `${MANUAL_UPDATE_IDS.help} ${MANUAL_UPDATE_IDS.error}` : MANUAL_UPDATE_IDS.help}
              aria-invalid={jsonError ? true : undefined}
              aria-labelledby={MANUAL_UPDATE_IDS.title}
              className="form-control"
              id={MANUAL_UPDATE_IDS.field}
              readOnly={!!(this.state.manualUpdateSession && this.state.manualUpdateSession.saving)}
              ref={(el) => { this.manualUpdateField = el; }}
              rows={20}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              value={manualUpdateJson}
              onChange={this.handleManualUpdateChange}
            />
            {jsonError && (
              <div className="alert alert-danger" id={MANUAL_UPDATE_IDS.error} role="alert" style={{ marginTop: '10px' }}>
                <strong><FormattedMessage defaultMessage="JSON Error:" id="snapshotTesting.jsonErrorLabel" /></strong> {jsonError}
              </div>
            )}
            {this.renderEditorSessionStatus('manualUpdateSession', this.handleOpenManualUpdate)}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.handleCloseManualUpdate}>
              <FormattedMessage defaultMessage="Cancel" id="common.cancel" />
            </Button>
            <Button
              bsStyle="primary"
              disabled={!!manualUpdateSession && (manualUpdateSession.saving || this.isSessionStale(manualUpdateSession))}
              onClick={this.handleSaveManualUpdate}
            >
              <i className="fa fa-save" /> <FormattedMessage defaultMessage="Save" id="common.save" />
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditSnapshotModal} onHide={this.handleCloseEditSnapshot} bsSize="large" aria-labelledby={EDIT_SNAPSHOT_IDS.title}>
          <Modal.Header closeButton>
            <Modal.Title id={EDIT_SNAPSHOT_IDS.title}>
              <FormattedMessage defaultMessage="Edit Snapshot (environmentState)" id="snapshotTesting.editSnapshotTitle" />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p id={EDIT_SNAPSHOT_IDS.help} style={{ color: '#8a6d3b' }}>
              <i className="fa fa-exclamation-triangle" />{' '}
              <FormattedMessage
                defaultMessage="Edit the snapshot's environmentState JSON directly. Useful for adjusting timestamps (e.g. flowInvocationCreatedAt) to fix time-sensitive replays."
                id="snapshotTesting.editSnapshotHelp"
              />
            </p>
            <div className="checkbox">
              <label htmlFor="snapshot-additional-channel-writes">
                <input
                  checked={this.getAdditionalChannelWritesDraft() === true}
                  disabled={!!(editSnapshotSession && editSnapshotSession.saving) || this.getAdditionalChannelWritesDraft() === null}
                  id="snapshot-additional-channel-writes"
                  onChange={this.handleAdditionalChannelWritesChange}
                  type="checkbox"
                />{' '}
                <FormattedMessage {...reviewMessages.allowAdditionalChannelWrites} />
              </label>
            </div>
            <p className="help-block">
              <FormattedMessage {...reviewMessages.allowAdditionalChannelWritesHelp} />
            </p>
            <textarea
              aria-describedby={editSnapshotJsonError ? `${EDIT_SNAPSHOT_IDS.help} ${EDIT_SNAPSHOT_IDS.error}` : EDIT_SNAPSHOT_IDS.help}
              aria-invalid={editSnapshotJsonError ? true : undefined}
              aria-labelledby={EDIT_SNAPSHOT_IDS.title}
              className="form-control"
              id={EDIT_SNAPSHOT_IDS.field}
              readOnly={!!(this.state.editSnapshotSession && this.state.editSnapshotSession.saving)}
              ref={(el) => { this.editSnapshotField = el; }}
              rows={28}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              value={editSnapshotJson}
              onChange={this.handleEditSnapshotChange}
            />
            {editSnapshotJsonError && (
              <div className="alert alert-danger" id={EDIT_SNAPSHOT_IDS.error} role="alert" style={{ marginTop: '10px' }}>
                <strong><FormattedMessage defaultMessage="JSON Error:" id="snapshotTesting.jsonErrorLabel" /></strong> {editSnapshotJsonError}
              </div>
            )}
            {this.renderEditorSessionStatus('editSnapshotSession', this.handleOpenEditSnapshot)}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.handleCloseEditSnapshot}>
              <FormattedMessage defaultMessage="Cancel" id="common.cancel" />
            </Button>
            <Button
              bsStyle="warning"
              onClick={this.handleSaveEditSnapshot}
              disabled={!!editSnapshotJsonError || (!!editSnapshotSession && (editSnapshotSession.saving || this.isSessionStale(editSnapshotSession)))}
            >
              <i className="fa fa-save" /> <FormattedMessage defaultMessage="Save Snapshot" id="snapshotTesting.saveSnapshot" />
            </Button>
          </Modal.Footer>
        </Modal>
      </AppPanel>
    );
  }
}

export default injectIntl(FlowTestResultView);
